import { useState } from 'react';
import { Box, Stack, Typography } from "@mui/material";
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import useGetUser from "./useGetUser";
import useCancelBooking, { CancelOutcome } from '../../api/useCancelBooking';
import Page from '../../components/Page';
import Loading from '../../components/Loading';
import Article from '../../components/Article';
import CustomButton from '../../components/CustomButton';
import BookingItem from './BookingItem';
import BookingFilter, { BookingFilterValue, filterBookings } from './BookingFilter';
import ProfileSummary from './ProfileSummary';
import MarketingConsentCard from './MarketingConsentCard';
import { StorageKeys } from '../../services/constants/StorageKeys';
import { storage } from "../../services/storageService";
import { useSnackbar } from "../../contexts/SnackBarContext";
import { useTenant } from '../../contexts/TenantContext';
import { Routes } from '../../routes/RouteConstants';
import { sectionTitleSx, tint } from '../../utils/textStyles';
import { useNavigate } from 'react-router-dom';
import CancelBookingDialog from './CancelBookingDialog';

// Presentation only: the list arrives in whatever order the server holds it. On "Aktív" what a
// guest looks for is the next appointment: everything still to come is listed first, soonest
// first; anything past follows, most recent first.
const byRelevance = (bookings) => {
  const today = dayjs().startOf("day");
  const isPast = (booking) => dayjs(booking?.date).isBefore(today);

  return [...bookings].sort((left, right) => {
    if (isPast(left) !== isPast(right)) return isPast(left) ? 1 : -1;

    const difference = dayjs(left?.date).valueOf() - dayjs(right?.date).valueOf();
    const order = isPast(left) ? -1 : 1;

    return difference === 0
      ? String(left?.startTime).localeCompare(String(right?.startTime)) * order
      : difference * order;
  });
};

// "Összes" is the guest's history: one timeline by appointment time, from the future to the past.
const byTimeDescending = (bookings) => [...bookings].sort((left, right) => {
  const difference = dayjs(right?.date).valueOf() - dayjs(left?.date).valueOf();
  return difference === 0
    ? String(right?.startTime).localeCompare(String(left?.startTime))
    : difference;
});

const sortFor = (filter) => (filter === BookingFilterValue.ALL ? byTimeDescending : byRelevance);

const ProfilePage = () => {
  const { getUser, data: getUserData, loading: getUserLoading } = useGetUser();
  const { cancelBooking, loading: cancelBookingLoading } = useCancelBooking();
  const { displayConfig } = useTenant();
  const showSnackbar = useSnackbar();
  const navigate = useNavigate();
  const [isDialogOpen, setOpen] = useState(false);
  const [filter, setFilter] = useState(BookingFilterValue.ACTIVE);

  const bookingToBeCancelled = storage.get(StorageKeys.BOOKING_TO_BE_CANCELLED);
  const bookings = getUserData?.bookings ?? [];
  const visibleBookings = filterBookings(bookings, filter);

  const handleOpenDialog = (bookingId) => {
    storage.set(StorageKeys.BOOKING_TO_BE_CANCELLED, bookingId);
    setOpen(true);
  };

  const rejectBookingCancelation = () => {
    storage.remove(StorageKeys.BOOKING_TO_BE_CANCELLED);
    setOpen(false);
  };

  // Straight to the calendar when a service is still picked from an earlier visit, to the
  // service list otherwise. Asked at click time: the pick lives in localStorage, not in state.
  // Going to BOOKING regardless would work too, but ProtectedBookingRoute's redirect pushes
  // a history entry the back button then keeps bouncing off.
  const startNewBooking = () => {
    navigate(storage.get(StorageKeys.SELECTED_SUPPLY) ? Routes.BOOKING : Routes.SUPPLY);
  };

  const handleConfirmCancel = async () => {
    setOpen(false);

    // Removed only once the call is back: the spinner on the booking reads the key on every render.
    const outcome = await cancelBooking(bookingToBeCancelled);
    storage.remove(StorageKeys.BOOKING_TO_BE_CANCELLED);
    if (outcome === CancelOutcome.FAILED) return;

    // GONE: removed meanwhile, by the tenant or from another tab. TOO_LATE: the cancellation
    // deadline passed while the page was open, so the card should now say so. CHANGED: it closed
    // meanwhile — rejected, or its time passed. The snackbar has told the guest either way, and
    // the list catches up the same way.
    if (outcome === CancelOutcome.CANCELLED) {
      showSnackbar("Sikeresen lemondtad a foglalást", "success");
    }
    getUser();
  };

  return (
    <Page>
      {getUserLoading && <Loading />}

      {!getUserLoading && getUserData && (
        <Stack width="100%" maxWidth="1000px" spacing={{ xs: 3, md: 4 }}>
          <ProfileSummary user={getUserData} />
          <MarketingConsentCard user={getUserData} />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} px={0.5}>
              <Typography variant="h5" fontWeight="bold" sx={sectionTitleSx(displayConfig)}>
                Foglalásaim
              </Typography>

              {visibleBookings.length > 0 && (
                <Typography
                  fontSize={13}
                  fontWeight={700}
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    color: displayConfig?.color,
                    backgroundColor: tint(displayConfig?.primaryColor, 0.14),
                    border: `1px solid ${tint(displayConfig?.primaryColor, 0.35)}`,
                  }}
                >
                  {`${visibleBookings.length} db`}
                </Typography>
              )}
            </Stack>

            {bookings.length > 0 && (
              <Box mb={2} px={0.5}>
                <BookingFilter value={filter} onChange={setFilter} />
              </Box>
            )}

            {visibleBookings.length > 0 ? (
              <Stack spacing={2}>
                {sortFor(filter)(visibleBookings).map((booking) => (
                  <BookingItem
                    key={booking.id}
                    booking={booking}
                    onDelete={handleOpenDialog}
                    loading={cancelBookingLoading && bookingToBeCancelled === booking?.id}
                  />
                ))}

                <Box pt={1}>
                  <CustomButton
                    text="Új időpont foglalása"
                    textTransform="none"
                    icon={<AddIcon fontSize="small" />}
                    onClick={startNewBooking}
                  />
                </Box>
              </Stack>
            ) : (
              <Article alignItems="center" xsHorizontalPadding={3} width="100%" sx={{ py: { xs: 4, sm: 5 } }}>
                <EventBusyIcon sx={{ fontSize: 56, color: tint(displayConfig?.primaryColor, 0.7), mb: 2 }} />
                {/* With bookings that are all behind the guest, "Aktív" is empty but the history is not. */}
                <Typography fontWeight={700} fontSize={18} align="center" sx={{ color: displayConfig?.color }}>
                  {bookings.length > 0 ? 'Nincs aktív foglalásod' : 'Még nincs foglalásod'}
                </Typography>
                <Typography fontSize={14} align="center" mt={1} mb={3}
                  sx={{ color: tint(displayConfig?.color, 0.65) }}>
                  {bookings.length > 0
                    ? 'A korábbi foglalásaidat az „Összes” alatt találod.'
                    : 'Foglalj időpontot néhány kattintással, és itt fogod látni.'}
                </Typography>
                <CustomButton text="Foglalás" onClick={startNewBooking} />
              </Article>
            )}
          </Box>
        </Stack>
      )}

      <CancelBookingDialog
        open={isDialogOpen}
        onClose={rejectBookingCancelation}
        onConfirm={handleConfirmCancel}
      />
    </Page>
  );
};

export default ProfilePage;
